import React, { useEffect, useMemo, useState, useRef } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import { sidebarLinks } from '../../utils/sidebarLinks'
import { useSelector } from 'react-redux'
import CheckPermission from '../../hooks/CheckPermission'
import { useTranslation } from 'react-i18next'
import SearchField from '../../shared/SearchField'

const Sidebar = ({ setActiveSection }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = CheckPermission()

  const loggedUser = useSelector(state => state?.auth?.user)
  const isReceptionist = loggedUser?.role?.name_en === 'Receptionist'
  const isAdmin = loggedUser?.role?.name_en === 'Admin'
  const isManager = loggedUser?.role?.name_en === 'manager'
  const isAccountant = loggedUser?.role?.name_en === 'Accountant'
  const isRcmAssistant = loggedUser?.role?.name_en === 'Rcm Assistant'
  const isNurse = loggedUser?.role?.name_en === 'Nurse'
  const isPractitioner = loggedUser?.role?.name_en === 'Practitioner'

  const getRcmSortedLinks = links => {
    const filtered = links?.filter(item => item?.moduleName !== 'visit')
    const priorityOrder = ['patient', 'eligibility', 'approval_management']
    const priorityItems = []
    const remainingItems = []
    filtered?.forEach(item => {
      const idx = priorityOrder.indexOf(item?.moduleName)
      if (idx !== -1) {
        priorityItems[idx] = item
      } else {
        remainingItems.push(item)
      }
    })
    return [...priorityItems.filter(Boolean), ...remainingItems]
  }

  const [filterText, setFilterText] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true'
  })
  const searchRef = useRef(null)

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed)
  }, [collapsed])

  const allLinks = useMemo(() => {
    const links = sidebarLinks(
      t,
      isReceptionist,
      isAdmin,
      isManager,
      loggedUser,
      isAccountant,
      isNurse,
      isPractitioner,
      isRcmAssistant
    )

    return isRcmAssistant ? getRcmSortedLinks(links) : links
  }, [
    t,
    isReceptionist,
    isAdmin,
    isManager,
    loggedUser,
    isAccountant,
    isNurse,
    isPractitioner,
    isRcmAssistant
  ])

  const filterSideBarLinks = useMemo(() => {
    const search = filterText.trim().toLowerCase()
    const isCallCenter = loggedUser?.role?.name_en === 'Call Center'
    return allLinks.filter(
      item =>
        (hasPermission(item?.moduleName, 'get') ||
          (isCallCenter && item?.allowCallCenter)) &&
        item?.label?.toLowerCase()?.includes(search)
    )
  }, [allLinks, filterText, hasPermission, loggedUser])

  useEffect(() => {
    setSelectedIndex(-1)
  }, [filterSideBarLinks])

  useEffect(() => {
    const handleKeyDown = e => {
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyF') ||
        ((e.ctrlKey || e.metaKey) && e.code === 'KeyK')) {
        e.preventDefault()
        searchRef.current?.focus()
        return
      }

      if (document.activeElement === searchRef.current || selectedIndex !== -1) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedIndex(prev => (prev < filterSideBarLinks.length - 1 ? prev + 1 : prev))
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0))
        } else if (e.key === 'Enter' && selectedIndex !== -1) {
          e.preventDefault()
          const selectedLink = filterSideBarLinks[selectedIndex]
          if (selectedLink) {
            if (selectedLink.link === '/queue-dashboard') {
              window.open(selectedLink.link, '_blank')
            } else {
              navigate(selectedLink.link)
            }
            // setFilterText('')
            setSelectedIndex(-1)
            // searchRef.current?.blur()
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [filterSideBarLinks, selectedIndex, navigate])

  const handleFilter = e => setFilterText(e.target.value)

  return (
    <div className={`h-screen bg-primary flex flex-col transition-all duration-300 ${collapsed ? 'w-[70px]' : 'w-[180px] xl:w-[210px]'}`}>
      <div className={`flex items-center !pt-[10px] justify-center ${collapsed ? 'hidden' : ''}`}>
        <SearchField
          ref={searchRef}
          icon={filterText ? 'pi pi-times   cursor-pointer' : 'pi pi-search'}
          handleClearClick={() => setFilterText('')}
          value={filterText}
          onChange={handleFilter}
          className='!w-[90%] '
          placeholder={t('Search')}
        />
      </div>
      {collapsed && (
        <div className='flex items-center !pt-[10px] justify-center'>
          <button
            onClick={() => setCollapsed(false)}
            className='flex border border-white h-[30px] min-w-[30px] text-white justify-center rounded-full w-[30px] hover:bg-white hover:text-primary items-center transition-colors'
            title={t('Search')}
          >
            <i className='pi pi-search text-[14px]'></i>
          </button>
        </div>
      )}
      <div className='flex flex-col pt-4 overflow-y-auto flex-1'>
        {filterSideBarLinks?.map((data, index) => {
          const isSelected = index === selectedIndex
          return (
            <React.Fragment key={index}>
              {data?.link === '/queue-dashboard' ? (
                <a
                  href={data?.link}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={`flex flex-row text-white gap-2 group items-center pe-1 ps-3 py-3 sidebar-navlink ${isSelected ? 'bg-white/20' : ''} ${collapsed ? '!px-0 justify-center' : ''}`}
                  title={collapsed ? data?.label : ''}
                >
                  <div className={`flex border border-white h-[30px] text-white justify-center rounded-full min-w-[30px] w-[30px] group-hover:bg-white group-hover:!text-primary items-center ${isSelected ? 'bg-white !text-primary' : ''}`}>
                    {data?.icon}
                  </div>
                  {!collapsed && (
                    <p className='text-[14px] text-white font-[400] group-hover:text-white mt-1'>
                      {data?.label}
                    </p>
                  )}
                </a>
              ) : (
                <NavLink
                  to={data?.link}
                  className={`flex flex-row gap-2 group items-center pe-1 ps-3 py-2 sidebar-navlink ${isSelected ? 'bg-white/20' : ''} ${collapsed ? '!px-0 justify-center' : ''}`}
                  title={collapsed ? data?.label : ''}
                >
                  <div className={`flex border border-white h-[30px] text-white justify-center rounded-full min-w-[30px] w-[30px] group-hover:bg-white group-hover:!text-primary items-center ${isSelected ? 'bg-white !text-primary' : ''}`}>
                    {data?.icon}
                  </div>
                  {!collapsed && (
                    <p className='text-[14px] text-white font-[400] group-hover:text-white mt-1 '>
                      {data?.label}
                    </p>
                  )}
                </NavLink>
              )}
            </React.Fragment>
          )
        })}
      </div>

      <div className='mt-auto border-t border-white/10 p-1'>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className='w-full flex items-center justify-center p-1 text-white hover:bg-white/10 rounded-md transition-colors'
          title={collapsed ? t('Expand') : t('Collapse')}
        >
          <i className={`pi ${collapsed ? 'pi-angle-right' : 'pi-angle-left'} text-lg`}></i>
        </button>
      </div>
    </div>
  )
}

export default Sidebar
